import { SdkContext, toLegacyError } from '../internal'
import { listItems } from '@/lib/utils'
import type {
  BatchProgress,
  Certificate,
  CertificateFont,
  CertificateSpaceConfig,
  CertificateTemplate,
  IssueRequest,
  IssueResponse,
  PreviewResult,
  VerifiedCertificate,
  WhoCanIssue
} from '@/types/certificates'

export interface CertificateListParams {
  spaceId?: string
  templateId?: string
  batchId?: string
  status?: string
  q?: string
  page?: number
  limit?: number
}

export interface Paginated<T> {
  items: T[]
  pagination: { page: number; limit: number; total: number; totalPages: number } | null
}

/** F-42: certificates — the space rule, templates, issuance, the member's own and public verification. */
export class CertificatesResource {
  constructor(private ctx: SdkContext) {}

  /* ------------------------------------------------------------- space -- */

  async getSpace(spaceId: string): Promise<CertificateSpaceConfig> {
    return this.ctx.call({ httpMethod: 'GET', path: '/certificates/spaces/{spaceId}' }, { spaceId })
  }

  async updateSpace(spaceId: string, whoCanIssue: WhoCanIssue): Promise<CertificateSpaceConfig> {
    return this.ctx.call(
      { httpMethod: 'PUT', path: '/certificates/spaces/{spaceId}', hasBody: true },
      { spaceId, body: { whoCanIssue } }
    )
  }

  async listFonts(): Promise<CertificateFont[]> {
    return listItems(await this.ctx.raw({ httpMethod: 'GET', path: '/certificates/fonts' }))
  }

  /* --------------------------------------------------------- templates -- */

  async listTemplates(spaceId: string, status?: string): Promise<CertificateTemplate[]> {
    return listItems(
      await this.ctx.raw(
        { httpMethod: 'GET', path: '/certificates/templates' },
        { query: { spaceId, status } }
      )
    )
  }

  /**
   * The active templates of every certificates space of the community, for the picker of
   * another module (F-64 3A). The platform default comes first; `q` filters by name.
   */
  async listCommunityTemplates(q?: string): Promise<CertificateTemplate[]> {
    return listItems(
      await this.ctx.raw(
        { httpMethod: 'GET', path: '/certificates/templates' },
        { query: { q: q?.trim() || undefined } }
      )
    )
  }

  async getTemplate(id: string): Promise<CertificateTemplate> {
    return this.ctx.call({ httpMethod: 'GET', path: '/certificates/templates/{id}' }, { id })
  }

  async createTemplate(body: Partial<CertificateTemplate> & { spaceId: string; name: string; kind: string; title: string }): Promise<CertificateTemplate> {
    return this.ctx.call({ httpMethod: 'POST', path: '/certificates/templates', hasBody: true }, { body })
  }

  async updateTemplate(id: string, body: Partial<CertificateTemplate>): Promise<CertificateTemplate> {
    return this.ctx.call({ httpMethod: 'PUT', path: '/certificates/templates/{id}', hasBody: true }, { id, body })
  }

  async activateTemplate(id: string): Promise<CertificateTemplate> {
    return this.ctx.call({ httpMethod: 'POST', path: '/certificates/templates/{id}/activate', hasBody: true }, { id, body: {} })
  }

  async archiveTemplate(id: string): Promise<CertificateTemplate> {
    return this.ctx.call({ httpMethod: 'POST', path: '/certificates/templates/{id}/archive', hasBody: true }, { id, body: {} })
  }

  async duplicateTemplate(id: string): Promise<CertificateTemplate> {
    return this.ctx.call({ httpMethod: 'POST', path: '/certificates/templates/{id}/duplicate', hasBody: true }, { id, body: {} })
  }

  /**
   * The server render of a template, with unsaved edits applied.
   *
   * Not through `client.request`: the official transport reads every non-JSON
   * body with `response.text()`, which mangles a PNG. This goes straight to
   * fetch with the same headers the client would send, and hands back an
   * object URL the caller must revoke.
   */
  async preview(id: string, draft: Partial<CertificateTemplate>, signal?: AbortSignal): Promise<PreviewResult> {
    const client = await this.ctx.ready
    const state = client.config
    const headers: Record<string, string> = { 'Content-Type': 'application/json', Accept: 'image/png' }
    if (state.token) headers.Authorization = 'Bearer ' + state.token
    if (state.communityId) headers['X-CommunityId'] = state.communityId
    if (state.memberId) headers['X-MemberId'] = state.memberId
    if (state.language) headers['Accept-Language'] = state.language

    const base = String(state.baseUrl || '').replace(/\/+$/, '')
    const response = await fetch(`${base}/api/certificates/templates/${encodeURIComponent(id)}/preview`, {
      method: 'POST',
      headers,
      body: JSON.stringify(draft),
      signal
    })

    if (!response.ok) {
      const payload = await response.json().catch(() => ({}))
      throw toLegacyError({
        name: 'MemberfyError',
        message: payload?.message || response.statusText,
        status: response.status,
        body: payload
      })
    }

    /* `[{ field, code }]`: the layout fields whose text did not fit at the
       minimum size. Reduced to the field keys, once each. */
    let warnings: string[] = []
    try {
      const raw = response.headers.get('X-Certificate-Warnings')
      const parsed = raw ? JSON.parse(raw) : []
      warnings = Array.isArray(parsed)
        ? Array.from(new Set(parsed.map((w: any) => (typeof w === 'string' ? w : w?.field)).filter(Boolean).map(String)))
        : []
    } catch {
      warnings = []
    }

    return { url: URL.createObjectURL(await response.blob()), warnings }
  }

  async uploadBackground(templateId: string, file: File): Promise<{
    url: string
    width: number
    height: number
    visibleArea: CertificateTemplate['visibleArea']
    layoutWarnings: { param: string; message: string }[]
    template: CertificateTemplate
  }> {
    const fd = new FormData()
    fd.append('background', file)
    return this.ctx.call(
      { httpMethod: 'POST', path: '/upload/certificate-template/{templateId}/background', hasBody: true },
      { templateId, body: fd }
    )
  }

  async uploadSignature(templateId: string, file: File): Promise<{ url: string }> {
    const fd = new FormData()
    fd.append('signature', file)
    return this.ctx.call(
      { httpMethod: 'POST', path: '/upload/certificate-template/{templateId}/signature', hasBody: true },
      { templateId, body: fd }
    )
  }

  /* ---------------------------------------------------------- issuance -- */

  async issue(body: IssueRequest): Promise<IssueResponse> {
    return this.ctx.call({ httpMethod: 'POST', path: '/certificates', hasBody: true }, { body })
  }

  async getBatch(id: string): Promise<BatchProgress> {
    return this.ctx.call({ httpMethod: 'GET', path: '/certificates/batches/{id}' }, { id })
  }

  async list(params: CertificateListParams): Promise<Paginated<Certificate>> {
    const result: any = await this.ctx.raw({ httpMethod: 'GET', path: '/certificates' }, { query: params })
    return { items: listItems<Certificate>(result), pagination: result?.pagination ?? null }
  }

  async revoke(id: string, reason: string): Promise<Certificate> {
    return this.ctx.call({ httpMethod: 'POST', path: '/certificates/{id}/revoke', hasBody: true }, { id, body: { reason } })
  }

  async reissue(id: string): Promise<{ revokedId: string; certificate: Certificate }> {
    return this.ctx.call({ httpMethod: 'POST', path: '/certificates/{id}/reissue', hasBody: true }, { id, body: {} })
  }

  /**
   * Deletes a revoked or failed certificate for good: the code stops existing
   * and verification answers "not found". 409 when it is still valid or
   * still rendering.
   */
  async remove(id: string): Promise<{ id: string; code: string }> {
    return this.ctx.call({ httpMethod: 'DELETE', path: '/certificates/{id}' }, { id })
  }

  async resendEmail(id: string): Promise<unknown> {
    return this.ctx.call({ httpMethod: 'POST', path: '/certificates/{id}/resend-email', hasBody: true }, { id, body: {} })
  }

  async retry(id: string): Promise<unknown> {
    return this.ctx.call({ httpMethod: 'POST', path: '/certificates/{id}/retry', hasBody: true }, { id, body: {} })
  }

  /* ------------------------------------------------------------ member -- */

  async mine(spaceId?: string): Promise<Certificate[]> {
    return listItems(
      await this.ctx.raw({ httpMethod: 'GET', path: '/certificates/mine' }, { query: { spaceId } })
    )
  }

  async get(id: string): Promise<Certificate> {
    return this.ctx.call({ httpMethod: 'GET', path: '/certificates/{id}' }, { id })
  }

  async setIndexing(id: string, indexable: boolean): Promise<Certificate> {
    return this.ctx.call(
      { httpMethod: 'PATCH', path: '/certificates/{id}/indexing', hasBody: true },
      { id, body: { indexable } }
    )
  }

  /* ------------------------------------------------------------ public -- */

  /** No session needed; the API answers 404 for a code that does not exist. */
  async verify(code: string): Promise<VerifiedCertificate> {
    return this.ctx.call({ httpMethod: 'GET', path: '/certificates/verify/{code}' }, { code })
  }
}
